// @ts-nocheck
import { useEffect, useState } from 'react';
import _ from 'lodash';
import Chip from '@mui/material/Chip';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RadioGroupItem, RadioGroup } from '@/components/ui/radio-group';

const DEFAULT_FIELD_TYPE_OPTIONS = [
  { value: 'text', label: 'Text', icon: '📝' },
  { value: 'select', label: 'Dropdown', icon: '📋' },
  { value: 'textarea', label: 'Text Area', icon: '📄' },
  { value: 'email', label: 'Email', icon: '✉️' },
  { value: 'phone', label: 'Phone', icon: '📱' },
  { value: 'jersey', label: 'Jersey #', icon: '🏃' },
  { value: 'date', label: 'Date', icon: '📅' },
];

// Map fieldType → primitiveType. Mirrors the per-type branches that previously
// lived inline in LeagueSecondStep.
const PRIMITIVE_BY_TYPE = {
  jersey: 'number',
};
const defaultPrimitive = (fieldType) => PRIMITIVE_BY_TYPE[fieldType] || 'string';

const THEME_CLASSES = {
  amber:
    'bg-gradient-to-r from-amber-500 to-orange-500 shadow-amber-500/25 hover:from-amber-600 hover:to-orange-600',
  slate:
    'bg-gradient-to-r from-foreground to-foreground/80 shadow-foreground/25 hover:from-foreground/90 hover:to-foreground/70',
};

/**
 * Reusable "Add a custom field" form. Owns its own draft state (name, type,
 * helper, required, options) and emits a built RegistrationField via onAdd
 * when the user clicks the button. Shared by:
 *   - apps/admin/src/containers/CreateLeague/LeagueSecondStep.tsx (registration
 *     step 2 — adds to Registration.fields)
 *   - apps/admin/src/containers/SettingAdmin/ProfileFieldsEditor.tsx (Settings →
 *     Users — adds to Organization.settings.profileFields)
 *
 * The parent only owns the destination array; this component is stateless w.r.t.
 * existing fields.
 */
export default function CustomFieldAdder({
  onAdd,
  theme = 'slate',
  fieldTypeOptions = DEFAULT_FIELD_TYPE_OPTIONS,
  onPendingChange,
}) {
  const [fieldType, setFieldType] = useState('text');
  const [name, setName] = useState('');
  const [helperText, setHelperText] = useState('');
  const [required, setRequired] = useState(false);
  const [options, setOptions] = useState([]);

  // Surface the in-progress name to the parent so flows like LeagueSecondStep's
  // "you started adding a field but haven't added it" Continue-guard can read
  // the latest value without prop-drilling state up.
  useEffect(() => {
    onPendingChange?.(name);
  }, [name, onPendingChange]);

  const onAddClick = () => {
    const field = {
      name,
      fieldId: _.snakeCase(name),
      helperText,
      required,
      fieldType,
      primitiveType: defaultPrimitive(fieldType),
      permanent: false,
      ...(fieldType === 'select' ? { options } : {}),
    };
    onAdd(field);
    setName('');
    setHelperText('');
    setFieldType('text');
    setRequired(false);
    setOptions([]);
  };

  const buttonTheme = THEME_CLASSES[theme] || THEME_CLASSES.slate;

  return (
    <div>
      {/* Field Type Selection */}
      <div className="mb-6">
        <Label className="mb-3 block text-sm font-medium text-foreground">Field Type</Label>
        <RadioGroup
          value={fieldType}
          onValueChange={(v) => {
            setFieldType(v);
            if (v === 'phone') {
              setName('Phone Number');
            } else if (v === 'jersey') {
              setName('Jersey Number');
            } else if (v === 'email') {
              setName('Email');
            }
          }}
          className="flex flex-wrap gap-2"
        >
          {fieldTypeOptions.map((option) => (
            <div key={option.value} className="flex items-center">
              <RadioGroupItem id={`cfa-${option.value}`} value={option.value} className="peer sr-only" />
              <Label
                htmlFor={`cfa-${option.value}`}
                className="flex cursor-pointer items-center gap-2 rounded-xl border-2 border-border bg-card px-4 py-2.5 text-sm font-medium text-muted-foreground transition-all hover:border-input hover:bg-muted/50 peer-data-[state=checked]:border-foreground peer-data-[state=checked]:bg-foreground peer-data-[state=checked]:text-white"
              >
                {option.icon && <span>{option.icon}</span>}
                {option.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      {/* Field Name and Placeholder */}
      <div className="mb-5 grid gap-4 md:grid-cols-2">
        <div>
          <Label className="mb-2 block text-sm font-medium text-foreground">Field Name</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Age, Skill Level, Notes"
            className="h-12 rounded-xl border-border bg-muted/50 px-4 text-base transition-all focus:border-ring focus:bg-background focus:ring-2 focus:ring-ring/10"
          />
        </div>
        <div>
          <Label className="mb-2 block text-sm font-medium text-foreground">Placeholder Text</Label>
          <Input
            value={helperText}
            onChange={(e) => setHelperText(e.target.value)}
            placeholder="e.g., Enter your age"
            className="h-12 rounded-xl border-border bg-muted/50 px-4 text-base transition-all focus:border-ring focus:bg-background focus:ring-2 focus:ring-ring/10"
          />
        </div>
      </div>

      {/* Required Toggle */}
      <div className="mb-5 flex items-center gap-3 rounded-xl bg-muted/50 p-4">
        <Switch id="cfa-required" onCheckedChange={(v) => setRequired(v)} checked={required} />
        <Label htmlFor="cfa-required" className="cursor-pointer text-sm font-medium text-foreground">
          Required field
          <span className="ml-2 text-muted-foreground/60">(Players must fill this out)</span>
        </Label>
      </div>

      {/* Dropdown Options */}
      {fieldType === 'select' && (
        <div className="mb-5">
          <Label className="mb-2 block text-sm font-medium text-foreground">Dropdown Options</Label>
          <Autocomplete
            multiple
            id="cfa-options"
            options={options}
            value={options}
            onChange={(e, v) => setOptions(v)}
            freeSolo
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    variant="outlined"
                    label={option}
                    key={key}
                    {...tagProps}
                    sx={{
                      borderRadius: '8px',
                      borderColor: '#e2e8f0',
                      '& .MuiChip-deleteIcon': {
                        color: '#94a3b8',
                        '&:hover': { color: '#64748b' },
                      },
                    }}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Type an option and press Enter"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: '#f8fafc',
                    '& fieldset': { borderColor: '#e2e8f0' },
                    '&:hover fieldset': { borderColor: '#cbd5e1' },
                    '&.Mui-focused fieldset': { borderColor: '#0f172a', borderWidth: '2px' },
                  },
                }}
              />
            )}
          />
        </div>
      )}

      {/* Add Button */}
      <Button
        disabled={!name}
        onClick={onAddClick}
        className={`h-12 w-full rounded-xl text-base font-semibold shadow-lg transition-all hover:shadow-xl disabled:opacity-50 ${buttonTheme}`}
      >
        <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        Add Field
      </Button>
    </div>
  );
}
